import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { dashboardApi, claimsApi } from '../../services/api';
import { useWebSocket } from '../../context/WebSocketContext';
import { useAuth } from '../../context/AuthContext';
import { useAgency } from '../../context/AgencyContext';
import { logger } from '../../lib/logger';
import { isElevationRequired } from '../../lib/elevationErrors';

export const useDashboardData = () => {
  const { dashboardData } = useWebSocket();
  const { user } = useAuth();
  const { currentAgency, isAzaiStaff } = useAgency();
  const [stats, setStats] = useState(null);
  const [recentClaims, setRecentClaims] = useState([]);
  const [allClaims, setAllClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const [needsElevation, setNeedsElevation] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      // AZAI platform staff need an active elevation before tenant data
      // is readable. Skip the fetches (which would 403) and surface a
      // dedicated CTA instead of the misleading "Failed to load" toast.
      // Agency users always fetch; a real error still surfaces.
      if (isAzaiStaff && !currentAgency?.id) {
        if (!cancelled) {
          setNeedsElevation(true);
          setLoading(false);
        }
        return;
      }

      try {
        const [statsRes, claimsRes] = await Promise.all([
          dashboardApi.getStats(),
          claimsApi.getAll(),
        ]);
        if (cancelled) return;
        setStats(statsRes.data);
        setAllClaims(claimsRes.data);
        setRecentClaims(claimsRes.data.slice(0, 5));
        setNeedsElevation(false);
        setLoadError(null);
      } catch (error) {
        if (cancelled) return;
        if (isElevationRequired(error)) {
          // Not a real failure — expected when an AZAI staff hasn't
          // elevated for this agency yet. The AgencySwitcher already
          // renders the "Request elevation" flow; we just avoid the
          // toast and surface a banner-friendly flag.
          setNeedsElevation(true);
          setLoadError(null);
          logger.debug('[dashboard] elevation required for tenant fetch', {
            op: 'dashboard_fetch',
            agency_id: currentAgency?.id,
            entity_type: user?.entity_type,
          });
        } else {
          setNeedsElevation(false);
          setLoadError(error?.response?.data?.detail || 'Failed to load dashboard data');
          toast.error('Failed to load dashboard data');
          logger.error('[dashboard] fetch failed', {
            op: 'dashboard_fetch',
            status: error?.response?.status,
            agency_id: currentAgency?.id,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
    // Re-run when the effective tenant identity changes so switching
    // agency / gaining elevation naturally re-fetches.
  }, [currentAgency?.id, isAzaiStaff, user?.id, user?.entity_type]);

  // Merge WebSocket data with existing stats for fields that are present
  useEffect(() => {
    if (dashboardData && !dashboardData._lastEvent) {
      setStats((prev) => (prev ? { ...prev, ...dashboardData } : dashboardData));
    }
  }, [dashboardData]);

  const handleStatCardClick = (filterType, filterValue) => {
    if (activeFilter?.type === filterType && activeFilter?.value === filterValue) {
      setActiveFilter(null);
      return;
    }
    setActiveFilter({ type: filterType, value: filterValue });
  };

  const getFilteredClaims = () => {
    if (!activeFilter) return recentClaims;
    if (activeFilter.type === 'status') {
      return allClaims.filter((c) => c.status === activeFilter.value);
    }
    if (activeFilter.type === 'all') {
      return allClaims.slice(0, 20);
    }
    return recentClaims;
  };

  return {
    stats,
    recentClaims,
    loading,
    activeFilter,
    setActiveFilter,
    handleStatCardClick,
    getFilteredClaims,
    needsElevation,
    loadError,
  };
};
